import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { StatusPill, PriorityPill, TASK_STATUSES, fmtDate, TaskModal } from './TasksCommon';
import './Tasks.css';

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const target = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.round((target - now) / (1000 * 60 * 60 * 24));
}

function TaskCard({ t, onClick, highlight }) {
  const dl = daysUntil(t.due_date);
  let dlText = '—';
  let dlColor = '#6b7280';
  if (dl !== null) {
    if (dl < 0) { dlText = `просрочено ${-dl} дн.`; dlColor = '#dc2626'; }
    else if (dl === 0) { dlText = 'сегодня'; dlColor = '#dc2626'; }
    else if (dl === 1) { dlText = 'завтра'; dlColor = '#d97706'; }
    else if (dl <= 3) { dlText = `через ${dl} дн.`; dlColor = '#d97706'; }
    else { dlText = `${dl} дн.`; dlColor = '#059669'; }
  }
  return (
    <div className="mytask-card" style={{ borderColor: highlight || '#e5e7eb' }} onClick={onClick}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <StatusPill value={t.status} />
        <PriorityPill value={t.priority} />
      </div>
      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6, color: '#111' }}>{t.title}</div>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>{t.project_title}</div>
      <div style={{ fontSize: 12, marginTop: 6, display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ color: '#6b7280' }}>Срок: {fmtDate(t.due_date)}</span>
        <span style={{ color: dlColor, fontWeight: 600 }}>{dlText}</span>
      </div>
    </div>
  );
}

export default function MyTasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [openTaskId, setOpenTaskId] = useState(null);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getMyTasks();
      setTasks(res.data);
    } catch {
      setTasks([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const [pRes, uRes] = await Promise.all([api.getProjects(), api.getUsers()]);
        setProjects(pRes.data.results || pRes.data);
        const usersData = uRes.data.results || uRes.data;
        setUsers(usersData.map((u) => ({
          id: u.id,
          username: u.username,
          full_name: [u.last_name, u.first_name].filter(Boolean).join(' '),
          position: u.position,
        })));
      } catch { /* ignore */ }
    })();
  }, []);

  // Категоризация
  const active = tasks.filter((t) => t.status !== 'done' && t.status !== 'cancelled');
  const overdue = active.filter((t) => t.is_overdue);
  const dueSoon = active.filter((t) => {
    const d = daysUntil(t.due_date);
    return d !== null && d >= 0 && d <= 2;
  });
  const attention = [...new Map([...overdue, ...dueSoon].map((t) => [t.id, t])).values()];
  const inProgress = active.filter((t) => t.status === 'in_progress' && !attention.find((x) => x.id === t.id));
  const newTasks = active.filter((t) => t.status === 'new' && !attention.find((x) => x.id === t.id));
  const doneRecent = tasks.filter((t) => t.status === 'done').slice(0, 5);

  // Отфильтрованный список для таблицы
  const filteredTable = statusFilter ? tasks.filter((t) => t.status === statusFilter) : tasks;

  return (
    <div className="tasks-page">
      <div style={{ marginBottom: 8 }}>
        <Link to="/tasks" style={{ color: '#6b7280', textDecoration: 'none', fontSize: 14 }}>← Дашборд задач</Link>
      </div>
      <h1>Мои задачи</h1>

      {/* Инструкция для новых пользователей */}
      <div style={{
        background: '#f0f9ff',
        border: '1px solid #3b82f6',
        borderRadius: 8,
        padding: '12px 16px',
        marginBottom: 16,
        fontSize: 13,
      }}>
        <strong style={{ color: '#1e40af' }}>💡 Как работать с задачами:</strong>
        <ol style={{ margin: '6px 0 0 20px', padding: 0, color: '#1e3a8a' }}>
          <li><strong>Кликните на карточку задачи</strong> — откроется окно с деталями.</li>
          <li>Внутри нажмите <strong>«▶ Начать работу»</strong> когда приступаете. Статус сменится на «В работе».</li>
          <li>По ходу работы двигайте <strong>шкалу прогресса</strong> (0% → 100%). Можно писать комментарии и прикладывать файлы.</li>
          <li>Когда всё сделано — нажмите <strong>«✔ Завершить»</strong>. Начальник увидит что задача готова.</li>
        </ol>
      </div>

      {/* Счётчики */}
      <div className="tasks-dashboard-counters" style={{ marginBottom: 20 }}>
        <div className="tasks-counter-card overdue">
          <div className="val">{overdue.length}</div>
          <div className="lbl">Просрочено</div>
        </div>
        <div className="tasks-counter-card" style={{ borderLeft: '4px solid #d97706' }}>
          <div className="val" style={{ color: '#d97706' }}>{dueSoon.length}</div>
          <div className="lbl">Срок ближе 3 дней</div>
        </div>
        <div className="tasks-counter-card progress">
          <div className="val">{active.filter((t) => t.status === 'in_progress').length}</div>
          <div className="lbl">В работе</div>
        </div>
        <div className="tasks-counter-card done">
          <div className="val">{doneRecent.length}</div>
          <div className="lbl">Готово (недавно)</div>
        </div>
      </div>

      {/* Требуют внимания */}
      {attention.length > 0 && (
        <div className="tasks-panel" style={{ marginBottom: 16, borderLeft: '4px solid #dc2626' }}>
          <h3 style={{ color: '#dc2626' }}>🔴 Требуют внимания ({attention.length})</h3>
          <div className="mytask-grid">
            {attention.map((t) => (
              <TaskCard key={t.id} t={t} onClick={() => setOpenTaskId(t.id)} highlight="#dc2626" />
            ))}
          </div>
        </div>
      )}

      {/* Новые задачи */}
      {newTasks.length > 0 && (
        <div className="tasks-panel" style={{ marginBottom: 16, borderLeft: '4px solid #2563eb' }}>
          <h3 style={{ color: '#2563eb' }}>📥 Новые задачи ({newTasks.length})</h3>
          <div className="mytask-grid">
            {newTasks.map((t) => (
              <TaskCard key={t.id} t={t} onClick={() => setOpenTaskId(t.id)} highlight="#2563eb" />
            ))}
          </div>
        </div>
      )}

      {/* В работе */}
      {inProgress.length > 0 && (
        <div className="tasks-panel" style={{ marginBottom: 16, borderLeft: '4px solid #f59e0b' }}>
          <h3 style={{ color: '#f59e0b' }}>🟡 В работе ({inProgress.length})</h3>
          <div className="mytask-grid">
            {inProgress.map((t) => (
              <TaskCard key={t.id} t={t} onClick={() => setOpenTaskId(t.id)} />
            ))}
          </div>
        </div>
      )}

      {/* Полная таблица */}
      <div className="tasks-panel">
        <h3>Все мои задачи ({tasks.length})</h3>
        <div className="tasks-toolbar">
          <select
            className="tasks-select"
            style={{ maxWidth: 220 }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">Все статусы</option>
            {TASK_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="tasks-empty">Загрузка…</div>
        ) : filteredTable.length === 0 ? (
          <div className="tasks-empty">Задач нет</div>
        ) : (
          <table className="tasks-table">
            <thead>
              <tr>
                <th>Задача</th>
                <th>Проект</th>
                <th>Статус</th>
                <th>Приоритет</th>
                <th>Срок</th>
              </tr>
            </thead>
            <tbody>
              {filteredTable.map((t) => (
                <tr key={t.id} className={t.is_overdue ? 'overdue' : ''} onClick={() => setOpenTaskId(t.id)}>
                  <td>{t.title}</td>
                  <td>{t.project_title}</td>
                  <td><StatusPill value={t.status} /></td>
                  <td><PriorityPill value={t.priority} /></td>
                  <td>{fmtDate(t.due_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {openTaskId && (
        <TaskModal
          taskId={openTaskId}
          projects={projects}
          users={users}
          onClose={() => setOpenTaskId(null)}
          onSaved={() => { setOpenTaskId(null); load(); }}
        />
      )}
    </div>
  );
}
